import { useRef, useCallback } from 'react';
import { Box, Flex } from '@chakra-ui/react';
import Actions from './actions';
import ChatBox from './ChatBox';
import ChatWithModals from './ChatWithModals';
import Header from './header';
import ConversationTabs from './ConversationTabs';
import CopilotModeTabs from './CopilotModeTabs';
import EmptyConversationState from './EmptyConversationState';
import useCopilotChatContext from '../hooks/useCopilotChatContext';
import { ChatDataProvider } from '../contexts/ChatDataContext';
import { ConversationTabInfo } from 'src/services/campaign-copilot';

const HEADER_HEIGHT = '60px';

const FusionCopilotContent = () => {
	const {
		messages,
		isCampaignMode,
		campaignModeContext,
		availableCampaignContext,
		enterCampaignMode,
		campaignConversations,
		activeCampaignConversationIndex,
		switchCampaignConversation,
		createCampaignConversation,
		deleteCampaignConversation,
		renameCampaignConversation,
		isCreatingCampaignConversation,
		exitCampaignMode,
		// Fusion multi-tab support
		fusionConversations,
		activeFusionConversationIndex,
		switchFusionConversation,
		deleteFusionConversation,
		createFusionConversation,
		isCreatingFusionConversation,
	} = useCopilotChatContext();

	// Check if we should show empty state (no Fusion conversations and not in campaign mode)
	const showEmptyState = !isCampaignMode && fusionConversations.length === 0;

	// Debug logging
	console.log('[FusionCopilotContent] Render - isCampaignMode:', isCampaignMode, 'availableCampaignContext:', availableCampaignContext);
	const chatBoxRef = useRef<HTMLDivElement>(null);
	const scrollContainerRef = useRef<HTMLDivElement>(null);

	// Convert campaign conversations to the format expected by ConversationTabs
	const conversationTabsData: ConversationTabInfo[] = campaignConversations.map((conv) => ({
		conversationId: conv.conversationId,
		title: conv.title,
		lastMessageAt: typeof conv.lastMessageAt === 'string' ? conv.lastMessageAt : conv.lastMessageAt.toISOString(),
		isActive: conv.isActive,
	}));

	// Handler to enter campaign mode from available context
	const handleEnterCampaignMode = useCallback(() => {
		if (availableCampaignContext) {
			enterCampaignMode(
				availableCampaignContext.campaignId,
				availableCampaignContext.campaignTitle,
				availableCampaignContext.fieldFocus
			);
		}
	}, [availableCampaignContext, enterCampaignMode]);

	return (
		<ChatWithModals>
			<Flex ref={chatBoxRef} h="full" direction="column" overflow="hidden">
				{/* Sticky header for mobile - hidden on desktop */}
				<Box display={{ base: 'block', md: 'none' }} flexShrink={0}>
					<Header height={HEADER_HEIGHT} />
				</Box>

				{/* Mode tabs - switch between Fusion Copilot tabs and Campaign Chat */}
				<Box flexShrink={0}>
					<CopilotModeTabs
						isCampaignMode={isCampaignMode}
						availableCampaignContext={availableCampaignContext}
						onEnterCampaignMode={handleEnterCampaignMode}
						onExitCampaignMode={exitCampaignMode}
						fusionConversations={fusionConversations}
						activeFusionConversationIndex={activeFusionConversationIndex}
						onSwitchFusionConversation={switchFusionConversation}
						onDeleteFusionConversation={deleteFusionConversation}
					/>
				</Box>

				{/* Campaign mode header - only shown in campaign mode */}
				{isCampaignMode && (
					<Box flexShrink={0}>
						<ConversationTabs
							conversations={conversationTabsData}
							activeIndex={activeCampaignConversationIndex}
							isCampaignMode={isCampaignMode}
							campaignTitle={campaignModeContext?.campaignTitle}
							onSwitchTab={switchCampaignConversation}
							onCreateTab={() => createCampaignConversation()}
							onDeleteTab={deleteCampaignConversation}
							onRenameTab={renameCampaignConversation}
							onExitCampaignMode={exitCampaignMode}
							isCreating={isCreatingCampaignConversation}
						/>
					</Box>
				)}

				{/* Show empty state or chat content */}
				{showEmptyState ? (
					<EmptyConversationState
						onCreateConversation={createFusionConversation}
						isCreating={isCreatingFusionConversation}
					/>
				) : (
					<>
						{/* Chat messages - scrollable area */}
						<Box
							ref={scrollContainerRef}
							flex="1"
							overflow="auto"
							position="relative"
							sx={{
								// Prevent iOS bounce scroll from interfering with our scroll detection
								WebkitOverflowScrolling: 'touch',
								// Ensure smooth scrolling on mobile
								scrollBehavior: 'smooth',
							}}
						>
							<ChatBox messages={messages} scrollContainerRef={scrollContainerRef} />
						</Box>

						{/* Actions - sticky at bottom */}
						<Box flexShrink={0}>
							<Actions />
						</Box>
					</>
				)}
			</Flex>
		</ChatWithModals>
	);
};

const FusionCopilot = () => {
	return (
		<ChatDataProvider>
			<FusionCopilotContent />
		</ChatDataProvider>
	);
};

export default FusionCopilot;
